/**
 * Logical container handling the navigation of questions.
 * At the moment we're not using redux for state management so everything is being defered back up the DOM scope.
 *
 *
 * @author Matthew Rawson @ 13/02/2018 14:13
 * @description
 * @notes
 */
import React, {Component} from "react"
import PropTypes from "prop-types"
import styled from "styled-components"
import {get} from "lodash"

import id from "@irati/utils/id"
import connect from "@irati/store/connect"
import notify from "@irati/utils/notify"
import {userSurveyTracking} from "@irati-tracking/insights"

import {FINISHED} from "@irati/insights/constants/userSurveyStatus"
import {
  getFirstUnansweredQuestion,
  getNextQuestion,
  getPreviousQuestion,
  getQuestionByIndex,
  getPatchObjectAfterAnsweringQuestion,
  getTransitionDirectionFromNavigation
} from "@irati/insights/helpers/question"
import {TRIGGER_OUTRO} from "@irati/insights/constants/question"
import FooterNav from "./FooterNav"
import Question from "./Question"

import {setNextQuestion, updateTransitionState} from "../../store/actions/question"

const _PATCH_SAVE_ERROR_MESSAGE = "Your answer couldn't be saved, please try again."

const Wrapper = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  min-height: calc(100vh - 141px);
`

const QuestionWrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
`

class QuestionContainer extends Component {
  static propTypes = {
    survey: PropTypes.object.isRequired,
    userSurvey: PropTypes.object,
    updateUserSurvey: PropTypes.func,
    createUserSurveyComment: PropTypes.func,
    setUpcomingQuestion: PropTypes.func.isRequired,
    setTriggerOutro: PropTypes.func.isRequired
  }
  static defaultProps = {
    userSurvey: {},
    updateUserSurvey: () => Promise.resolve(),
    createUserSurveyComment: () => Promise.resolve()
  }

  constructor(props) {
    super(props)
    this.state = {
      answerPendingSave: null
    }

    /** Set the initial question obj */
    this.getQuestion(getFirstUnansweredQuestion)
  }

  /**
   * Lifecycle
   * Render middleware to determine if the question set is finish to dispatch the user survey update
   *
   * @param nextProps
   * @returns {boolean}
   */
  shouldComponentUpdate(nextProps) {
    if (this.haltComponent) return false

    const questionObj = get(nextProps, "survey.questionObj")
    const {updateUserSurvey, createUserSurveyComment} = this.props

    /** If question isn't available assume all have been answered and the status is incorrect */
    if (!questionObj || !questionObj.question) {
      /** ignore future prop changes (stops erroneous multiple triggers on prop changes) */
      this.haltComponent = true

      /** User survey navigation */
      createUserSurveyComment().finally(() => {
        updateUserSurvey({status: FINISHED}, true)
      })
      return false
    }

    return true
  }

  /**
   * Helper method to always dispatch `updateUserSurvey` if the returned question is null. i.e. survey finished
   *
   * @param serviceMethod
   * @param args
   * @returns {*}
   */
  getQuestion(serviceMethod, ...args) {
    if (!serviceMethod) return

    const questionObj = serviceMethod(this.props.userSurvey, ...args)

    this.props.setUpcomingQuestion(questionObj)

    return questionObj
  }

  /**
   * Helper method to combine next and previous navigation with `getQuestion`
   *
   * @param serviceMethod
   * @param serviceArgs
   * @returns {*}
   */
  navigateQuestion(serviceMethod, ...serviceArgs) {
    /** If an answer is pending save, do this before naviagtion */
    if (this.state.answerPendingSave) {
      return this.answerQuestion(this.state.answerPendingSave, false, serviceMethod, ...serviceArgs)
    }

    /**
     * Default to using the `questionObj.question` as a service method parameters, except when custom args are provided
     */
    const getQuestionArgs = serviceArgs.length > 0 ? serviceArgs : [this.props.survey.questionObj.question]
    const questionObj = this.getQuestion(serviceMethod, ...getQuestionArgs)
    if (!questionObj) return

    this.setState({questionObj})
  }

  /**
   * Helper method to dispatch trigger outro before proxying all navigation args
   *
   * @param serviceArgs
   */
  triggerOutroAndNavigate(serviceMethod, ...serviceArgs) {
    /** Determine animation direction */
    const direction = getTransitionDirectionFromNavigation(this.props.survey.questionObj, serviceMethod, ...serviceArgs)

    /** Dispatch trigger outro */
    this.props.setTriggerOutro(direction)

    /** Proxy navigation */
    this.navigateQuestion(serviceMethod, ...serviceArgs)
  }

  /**
   * Takes the answer given and generates the patch obj then patches it to the API, setting the loading state to this
   * promise
   *
   * @todo re-reading this after coming back to it I feel like perhaps pending state should be moved into redux.
   * @todo following on from the above 27.09.2018... pendSave will be removed in the next refactor.
   * @param answer
   * @param pPendSave
   * @param navigateServiceMethod
   * @param serviceArgs
   * @returns {*}
   */
  answerQuestion(answer, pPendSave, navigateServiceMethod, ...serviceArgs) {
    /** Manually handle defaults because we want to maintain `null` values */
    const pendSave = pPendSave === undefined ? false : pPendSave
    const serviceMethod = navigateServiceMethod === undefined ? getNextQuestion : navigateServiceMethod

    if (pendSave) {
      this.setState({answerPendingSave: answer})
      /** n.b. rather than returning `undefined` anything expecting a promise from this function will still work */
      return Promise.resolve()
    }

    const {userSurvey, updateUserSurvey, survey} = this.props
    const {questionObj} = survey
    const {question} = questionObj
    const patch = getPatchObjectAfterAnsweringQuestion(userSurvey, question, answer)

    const patchAnswer = updateUserSurvey(patch)
      .then(() => {
        userSurveyTracking.answeredQuestion({userSurvey, questionObj, answer})

        /** Always remove any answer pending save regardless of if it's there */
        this.setState({answerPendingSave: null})

        /** Auto navigate */
        this.navigateQuestion(serviceMethod, ...serviceArgs)
      })
      .catch(() => {
        notify.error({message: _PATCH_SAVE_ERROR_MESSAGE, position: "top-left"})
      })

    this.setState({patchAnswer})

    return patchAnswer
  }

  generateNavigationTracking = ({modify, direct}) => {
    const {survey, userSurvey} = this.props
    const {questionObj} = survey
    userSurveyTracking.navigate({userSurvey, questionObj, modify, direct})
  }

  onNavigateNext = () => {
    this.generateNavigationTracking({modify: 1})
    this.triggerOutroAndNavigate(getNextQuestion)
  }

  onNavigatePrevious = () => {
    this.generateNavigationTracking({modify: -1})
    this.triggerOutroAndNavigate(getPreviousQuestion)
  }

  onNavigateByIndex = (index) => {
    this.generateNavigationTracking({direct: index + 1})
    this.triggerOutroAndNavigate(getQuestionByIndex, index)
  }

  render() {
    const idNamespace = id("question-container", this.props)
    const {survey = {}} = this.props
    const {questionObj = {}} = survey
    const {question = {}, questionNumber} = questionObj

    return (
      <Wrapper id={idNamespace}>
        <QuestionWrapper>
          {question && (
            <Question
              id={idNamespace}
              question={question}
              questionNumber={questionNumber}
              userSurvey={this.props.userSurvey}
              answerQuestion={(answer, pendSave, doNotNavigate) =>
                this.answerQuestion(answer, pendSave, doNotNavigate ? null : getNextQuestion)
              } />
          )}
        </QuestionWrapper>
        <FooterNav
          id={idNamespace}
          questionObj={questionObj}
          onNext={this.onNavigateNext}
          onBack={this.onNavigatePrevious}
          onSelectQuestionIndex={this.onNavigateByIndex}
          disabled={survey.transitioning} />
      </Wrapper>
    )
  }
}

const mapStateToProps = ({survey}) => ({survey})

export const mapDispatchToProps = (dispatch) => ({
  setUpcomingQuestion: (questionObj) => dispatch(setNextQuestion(questionObj)),
  setTriggerOutro: (direction) => dispatch(updateTransitionState(TRIGGER_OUTRO, direction))
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(QuestionContainer)
